import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OffersBlock from "@/components/seo/OffersBlock";
import RecentPagesBlock from "@/components/seo/RecentPagesBlock";
import PopularPagesBlock from "@/components/seo/PopularPagesBlock";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Swords, Flame, TrendingUp } from "lucide-react";
import heroUfc from "@/assets/hero-hub-ufc.webp";

const UfcHub = () => {
  const { data: fights } = useQuery({
    queryKey: ["ufc-fights-hub"],
    queryFn: async () => {
      const { data } = await supabase
        .from("ufc_fights")
        .select("*")
        .eq("published", true)
        .order("fight_date", { ascending: true })
        .limit(200);
      return data || [];
    },
  });

  return (
    <>
      <Helmet>
        <title>UFC & MMA en Streaming HD - Combats en Direct | RoiDuStreaming</title>
        <meta name="description" content="Regardez tous les combats UFC et MMA en streaming HD. Événements en direct sur RoiDuStreaming." />
        <link rel="canonical" href="https://roidustreaming.fr/ufc" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <section className="relative py-20 md:py-32 px-4 overflow-hidden">
          <img src={heroUfc} alt="UFC MMA streaming en direct HD" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" />

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/10 border border-destructive/20 text-destructive text-sm font-semibold mb-6">
              <TrendingUp className="w-4 h-4" />
              UFC & MMA en direct
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4 text-foreground leading-tight">
              UFC & MMA en <span className="text-destructive">Streaming</span>
              <br />
              <span className="text-2xl md:text-4xl font-bold text-muted-foreground">HD & 4K • Combats en direct • Tous les événements</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              UFC, Bellator, PFL et tous les événements MMA en direct, sans décalage.
            </p>
          </div>
        </section>

        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-foreground">Prochains combats en streaming</h2>
            {fights && fights.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {fights.map((fight) => (
                  <Link key={fight.id} to={`/ufc/${fight.slug}`} title={`${fight.fighter_a} vs ${fight.fighter_b} streaming en direct`}>
                    <Card className="p-4 bg-card border-primary/20 hover:border-primary/50 transition-all hover:scale-[1.02]">
                      <p className="text-xs text-primary font-semibold uppercase mb-1">{fight.event}</p>
                      <h3 className="text-lg font-bold text-foreground mb-1">
                        {fight.fighter_a} vs {fight.fighter_b} <span className="sr-only">streaming en direct</span>
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        🥊 {new Date(fight.fight_date).toLocaleDateString("fr-FR")}
                      </p>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-12">Aucun combat programmé pour le moment.</p>
            )}
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4">
          <OffersBlock />
        </div>

        <PopularPagesBlock />
        <RecentPagesBlock />

        <Footer />
      </div>
    </>
  );
};

export default UfcHub;
